<template>
  <div>

    <!-- Admin: See All Report -->
    <template>
      <!-- Admin: See All Report -->
      <template v-if="$permissionAbility(ATTENDANCE_SEE_ALL_REPORT, permissions)">
        <b-card>
          <!-- Filters -->
          <validation-observer ref="generateAllAttendanceValidation">
            <b-form @submit.prevent="generateAllAttendanceReport('preview')">
              <b-row class="mb-2">

                <!-- Employee -->
                <b-col
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  class="mb-2"
                >
                  <b-form-group label="" label-for="empId">
                    <ValidationProvider v-slot="{ errors }" name="User" vid="empId">
                      <v-select
                        id="empId"
                        v-model="selectEmpId"
                        :options="filterEmpIdOption"
                        :reduce="item => item.id"
                        label="name"
                        placeholder="Select Employee"
                        class="w-100"
                        @input="loadAttendance('user')"
                      >
                        <template #option="data">
                          <UserSelect :user="data" />
                        </template>
                      </v-select>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>

                <!-- Status -->
                <b-col
                  cols="12"
                  sm="6"
                  md="3"
                  lg="2"
                  class="mb-2"
                >
                  <v-select
                    v-model="filterStatusId"
                    :options="statusIdOption"
                    :reduce="item => item.value"
                    label="name"
                    placeholder="Select Status"
                    class="w-100"
                    @input="loadAttendance()"
                  />
                </b-col>

                <!-- Department -->
                <b-col
                  cols="12"
                  sm="6"
                  md="4"
                  lg="4"
                  class="mb-2"
                >
                  <v-select
                    v-model="selectDepartmentId"
                    :options="filterDepartmentIdOption"
                    :reduce="item => item.id"
                    label="name"
                    placeholder="Select Department"
                    class="w-100"
                    multiple
                    @input="loadAttendance('department')"
                  />
                </b-col>

                <!-- Date Range -->
                <b-col
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  class="mb-2"
                >
                  <flat-pickr
                    v-model="filterRangeDate"
                    class="form-control w-100"
                    placeholder="Select Date Range"
                    :config="{ mode: 'range', defaultDate: [filterStartDate, filterEndDate] }"
                    @input="loadAttendance('date')"
                  />
                </b-col>
              </b-row>

              <!-- Generate Button -->
              <div class="d-flex flex-wrap justify-content-end mt-2">
                <b-button
                  v-ripple.400="'rgba(255,255,255,0.15)'"
                  class="ml-1 mb-1 w-30 w-sm-auto"
                  variant="primary"
                  type="submit"
                  :disabled="!filterStartDate || !filterEndDate"
                >
                  <template v-if="isAttendanceReportGenerating">
                    <b-spinner small /> Report Generating ..
                  </template>
                  <template v-else>
                    Generate Report
                  </template>
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-card>
      </template>

      <!-- Employee: See Self Report -->
      <template v-else-if="$permissionAbility(ATTENDANCE_SEE_SELF_REPORT, permissions)">
        <b-card title="Personal Report">
          <b-row class="align-items-center">
            <!-- Date Picker -->
            <b-col
              cols="12"
              sm="8"
              md="6"
              lg="5"
              class="mb-2"
            >
              <flat-pickr
                v-model="filterRangeDate"
                class="form-control w-100"
                placeholder="Filter Date Range"
                :config="{ mode: 'range', defaultDate: [filterStartDate, filterEndDate] }"
              />
            </b-col>

            <!-- Generate Button -->
            <b-col
              cols="12"
              sm="4"
              md="3"
              lg="2"
              class="mb-2"
            >
              <b-button
                variant="primary"
                class="w-30"
                :disabled="!filterRangeDate"
                @click="generateAllAttendanceReport('preview')"
              >
                <template v-if="isAttendanceReportGenerating">
                  <b-spinner small /> Report Generating ..
                </template>
                <template v-else>
                  Generate Report
                </template>
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </template>
    </template>

    <b-card v-if="attendanceData">
      <!-- Attendance Preview -->
      <div
        class="attendance-preview p-3 border mx-auto col-12 col-md-10"
        :class="$store.state.app.isDark ? 'dark-layout' : ''"
      >

        <h3 class="text-center mb-4">
          {{ companyName }}
        </h3>

        <div
          v-for="(attendance, index) in attendanceData"
          :key="index"
          class="mb-5"
        >

          <!-- Employee Info -->
          <div class="table-responsive mb-2">
            <table class="table table-bordered text-center attendance-table mb-3">
              <tr>
                <td><b>Employee Name:</b></td>
                <td>{{ attendance.user.name }}</td>
                <td><b>Designation:</b></td>
                <td>{{ attendance.user.designation?.name }}</td>
                <td><b>Employee ID:</b></td>
                <td>{{ attendance.user.employee_number }}</td>
              </tr>
              <tr>
                <td><b>Division:</b></td>
                <td>{{ attendance.user.department?.division?.name }}</td>
                <td><b>Department:</b></td>
                <td>{{ attendance.user.department?.name }}</td>
                <td><b>Employee Type:</b></td>
                <td>{{ attendance.user.status ? attendance.user.employee_type?.name : 'Inactive' }}</td>
              </tr>
            </table>
          </div>

          <!-- Attendance Table -->
          <div class="table-responsive mb-2">
            <table
              class="table table-bordered text-center attendance-table mb-3"
              style="border-collapse:collapse;"
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Extra/Less Duty</th>
                  <th>Overtime</th>
                  <th>Total Worked</th>
                  <th>Expected Duty</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(day, i) in attendance.daily_attendances_reports"
                  :key="i"
                >
                  <td>{{ day.date }}</td>
                  <td>{{ day.sort_name }} <span
                    v-if="day.half_day_leave"
                    class="text-warning font-weight-bold"
                  >(HD)</span></td>
                  <td>{{ formatTime(day.first_check_in) }}</td>
                  <td>{{ formatTime(day.last_check_out) }}</td>
                  <td>{{ day.extra_less_duty_hours }}</td>
                  <td>{{ day.over_time_hours }}</td>
                  <td>{{ day.total_worked_hours }}</td>
                  <td>{{ day.expected_duty_hours }}</td>
                  <td>{{ day.remarks }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Attendance Summary -->
          <div class="text-center mb-2 font-weight-bold">
            Attendance Summary
          </div>
          <div class="table-responsive">
            <table class="table table-bordered info-summery-table mb-5 text-center">
              <tr>
                <td><b>Presents (P)</b></td>
                <td>{{ attendance.attendances_statuses?.presents?.attendance_status_count || 0 }}</td>
                <td><b>Full Day Leave (L)</b></td>
                <td>{{ attendance.attendances_statuses?.leaves?.attendance_status_count || 0 }}</td>
                <td><b>Expected Duty Hour</b></td>
                <td>{{ attendance.duty_hours?.expected_duty_hours || '00:00' }}</td>
              </tr>
              <tr>
                <td><b>Absent (A)</b></td>
                <td>{{ attendance.attendances_statuses?.absents?.attendance_status_count || 0 }}</td>
                <td><b>Half Day Leave (HD)</b></td>
                <td>{{ attendance.attendances_statuses?.half_day_leave?.attendance_status_count || 0 }}</td>
                <td><b>Total Work Hour</b></td>
                <td>{{ attendance.duty_hours?.total_worked_hours || '00:00' }}</td>
              </tr>
              <tr>
                <td><b>Delay (D)</b></td>
                <td>{{ attendance.attendances_statuses?.delays?.attendance_status_count || 0 }}</td>
                <td><b>Sandwich (S)</b></td>
                <td>{{ attendance.attendances_statuses?.sandwich?.attendance_status_count || 0 }}</td>
                <td><b>Extra/Less Duty Hours</b></td>
                <td>{{ attendance.duty_hours?.extra_less_duty_hours || '00:00' }}</td>
              </tr>
              <tr>
                <td><b>Extreme Delay (ED)</b></td>
                <td>{{ attendance.attendances_statuses?.extreme_delay?.attendance_status_count || 0 }}</td>
                <td><b>Available / Working Days</b></td>
                <td>{{ attendance.total_office_available_count || 0 }} / {{ attendance.total_working_days_count || 0 }}</td>
                <td><b>Total Over Time Hour</b></td>
                <td>{{ attendance.duty_hours?.total_over_time_hours || '00:00' }}</td>
              </tr>
              <tr>
                <td><b>Holiday (H)</b></td>
                <td>{{ attendance.attendances_statuses?.holidays?.attendance_status_count || 0 }}</td>
                <td><b>Weekend (W)</b></td>
                <td>{{ attendance.attendances_statuses?.weekends?.attendance_status_count || 0 }}</td>
                <td><b>Total Off Day OT</b></td>
                <td>{{ attendance.duty_hours?.total_off_days_over_time_hours || '00:00' }}</td>
              </tr>
            </table>
          </div>
        </div>

        <div class="text-right mt-3">
          <template v-if="isAttendanceReportDownloading">
            <b-button
              variant="success"
              disabled
            >
              <b-spinner small /> Downloading ..
            </b-button>
          </template>
          <template v-else>
            <b-button
              variant="success"
              @click="generateAllAttendanceReport('download')"
            >
              Download PDF
            </b-button>
          </template>
        </div>
      </div>
    </b-card>

  </div>
</template>
<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BButton,
  BForm,
  BModal,
  VBModal,
  BSpinner,
  BCol,
  BRow,
  BFormDatepicker,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import flatPickr from 'vue-flatpickr-component'
import { required, max } from '@validations'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapGetters } from 'vuex'
import {
  ATTENDANCE_SHOW,
  ATTENDANCE_SEARCH,
  ATTENDANCE_SEE_ALL_REPORT,
  ATTENDANCE_SEE_SELF_REPORT,
} from '@/helpers/permissionsConstant'
import { formatDateRange, minutesToHours } from '@/helpers/helpers'
import { EventBus } from '@/helpers/event-bus'
import strSnakeToPascal from '@/filter/strSnakeToPascal'
import UserSelect from '@/layouts/components/UserSelect.vue'

export default {
  name: 'AttendanceView',
  components: {
    UserSelect,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BCol,
    BRow,
    flatPickr,
    BFormDatepicker,
  },
  directives: {
    Ripple,
  },
  data() {
    return {

      attendanceData: null, // for HTML preview
      pdfBase64: null, // for download
      pdfUrl: null,
      companyName: null,

      // filter
      statusIdOption: [
        {
          name: 'Active',
          value: 'active',
        },
        {
          name: 'Inactive',
          value: 'inactive',
        },
      ],
      filterEmpTypeIdOption: [],
      filterStatusId: 'active',
      selectEmpTypeId: '',
      filterDivisionIdOption: [],
      selectDivisionId: '',
      filterRangeDate: null,
      filterDepartmentIdOption: [],
      selectDepartmentId: [],
      filterEmpIdOption: [],
      selectEmpId: '',
      filterStartDate: this.getCurrentDate(),
      filterEndDate: this.getCurrentDate(),
      filterSelfRangeDate: '',
      filterSelfStartDate: '',
      filterSelfEndDate: '',
      isAttendanceReportGenerating: false,
      isAttendanceReportDownloading: false,
      isAttendanceSelfReportGenerating: false,
      ATTENDANCE_SHOW,
      ATTENDANCE_SEARCH,
      ATTENDANCE_SEE_ALL_REPORT,
      ATTENDANCE_SEE_SELF_REPORT,
    }
  },

  computed: {
    ...mapGetters({
      permissions: 'userModule/getPermissions',
      authUser: 'userModule/getUser',
    }),
  },

  async created() {
    // EventBus.$on("changed-check-in-out", () => {
    //   if (this.$route?.name === "admin-attendance") {
    //     this.loadItems();
    //   }
    // });

    this.filterRangeDate = `${this.filterStartDate} to ${this.filterEndDate}`

    try {
      const currentDate = new Date()

      const todaysDate = `${currentDate.getFullYear()}-${(
        currentDate.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}-${currentDate
        .getDate()
        .toString()
        .padStart(2, '0')}`

      this.filterStartDate = todaysDate
      this.filterEndDate = todaysDate
      this.filterSelfStartDate = todaysDate
      this.filterSelfEndDate = todaysDate

      const [employeeTypes, divisions, departments, employees] = await Promise.all([
        this.getEmployeeTypes(),
        this.getDivisions(),
        this.getDepartments(),
        this.getActiveEmployees(),
      ])

      // employee type
      this.filterEmpTypeIdOption = (employeeTypes?.data?.data || []).map(
        item => {
          const name = item?.name
          return {
            name,
            id: item?.id,
          }
        },
      )

      // department
      this.filterDepartmentIdOption = (departments?.data?.data || []).map(
        item => {
          const name = item?.name

          return {
            name,
            divisionId: item?.division_id,
            id: item?.id,
          }
        },
      )

      // division
      this.filterDivisionIdOption = (divisions?.data?.data || []).map(
        item => {
          const name = item?.name
          return {
            name,
            id: item?.id,
          }
        },
      )

      // employee
      this.filterEmpIdOption = (employees?.data?.data || []).map(item => ({
        avatar: item?.avatar,
        name: item?.name,
        email: item?.email,
        mobile: item?.mobile,
        employee_number: item?.employee_number,
        id: item?.id,
        departmentId: item?.department_id,
      }))
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error',
          icon: 'BellIcon',
          variant: 'danger',
          text: error?.response?.data?.message,
        },
      })
    }
  },

  methods: {

    getCurrentDate() {
      // Input date string
      const currentDate = new Date()

      // Extract year, month, and day components
      const year = currentDate.getFullYear()
      const month = String(currentDate.getMonth() + 1).padStart(2, "0") // Add 1 to month since it's zero-based
      const day = String(currentDate.getDate()).padStart(2, "0")

      // Create the formatted date string
      return `${year}-${month}-${day}`
    },

    formatDate(date) {
      return date ? this.$moment(date).format('DD-MM-YYYY') : '-'
    },
    formatTime(time) {
      return time ? this.$moment(time, 'HH:mm:ss').format('LT') : '-'
    },

    handleDateRangeChange() {
      if (this.filterRangeDate && this.filterRangeDate !== '') {
        const dates = this.filterRangeDate.split(' to ')

        if (dates.length === 1) {
          // Case where only one date is provided
          this.filterStartDate = dates[0]
          this.filterEndDate = dates[0]
        } else if (dates.length === 2) {
          // Case where both start and end dates are provided
          this.filterStartDate = dates[0]
          this.filterEndDate = dates[1]
        } else {
          // Handle invalid date range format
          console.error('Invalid date range format')
          // You can choose to handle this case differently based on your requirements
        }
      }
    },

    async loadAttendance() {
      const employees = await this.getAllEmployees()
      this.filterEmpIdOption = (employees?.data?.data || []).map(item => ({
        status: item?.status,
        avatar: item?.avatar,
        name: item?.name,
        email: item?.email,
        mobile: item?.mobile,
        employee_number: item?.employee_number,
        id: item?.id,
        departmentId: item?.department_id,
      }))

      if (this.selectEmpId) {
        this.filterStatusId = ''
        this.selectDepartmentId = []
      }

      if (Array.isArray(this.selectDepartmentId) && this.selectDepartmentId.length > 0) {
        this.filterEmpIdOption = this.filterEmpIdOption.filter(item => this.selectDepartmentId.includes(item?.departmentId))
      }

      if (this.filterStatusId) {
        const status = this.filterStatusId === 'active'
        this.filterEmpIdOption = this.filterEmpIdOption.filter(
          item => item?.status === status,
        )
      }
    },

    async getActiveEmployees() {
      return await this.$api.get('api/users/active-all')
    },
    async getAllEmployees() {
      return await this.$api.get('api/users/all')
    },
    async getDivisions() {
      return await this.$api.get('api/divisions/all')
    },
    async getEmployeeTypes() {
      return await this.$api.get('api/employee-types/all')
    },
    async getDepartments() {
      return await this.$api.get('api/departments/all')
    },

    saveFile(file) {
      const blob = new Blob([file], { type: 'application/pdf' })
      this.pdfUrl = URL.createObjectURL(blob) // save blob url in data
    },

    downloadPDF() {
      if (!this.pdfBase64) return

      const link = document.createElement('a')
      link.href = `data:application/pdf;base64,${this.pdfBase64}`
      link.download = 'attendance-report.pdf'
      link.click()
    },

    async generateAllAttendanceReport(type = 'preview') {
      try {
        type === 'preview'
          ? (this.isAttendanceReportGenerating = true)
          : (this.isAttendanceReportDownloading = true)

        console.log(this.filterRangeDate)

        const { startDate, endDate } = formatDateRange(this.filterRangeDate)

        console.log(startDate)

        const empType = this.$permissionAbility(ATTENDANCE_SEE_ALL_REPORT, this.permissions) ? 'all' : 'self'

        const response = await this.$api.get(
          `api/attendances/report/${type === 'preview' ? 'preview' : 'download'}`,
          {
            params: {
              startDate,
              endDate,
              deptId: this.selectDepartmentId,
              empId: empType === 'self' ? this.authUser?.id : this.selectEmpId,
              status: this.filterStatusId,
            },
            responseType: type === 'preview' ? 'json' : 'text',
          },
        )

        if (type === 'download') {
          const pdfBase64 = response.data

          const byteArray = Uint8Array.from(atob(pdfBase64), c => c.charCodeAt(0))
          const blob = new Blob([byteArray], { type: 'application/pdf' })

          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = `attendance-report-${this.filterStartDate}-to-${this.filterEndDate}.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else {
          // save data for preview
          this.companyName = response.data.data.company_name
          this.attendanceData = response.data.data.attendanceReport
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'BellIcon',
            variant: 'success',
            text: 'Attendance Report Generated Successfully',
          },
        })
      } catch (error) {
        console.error(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'BellIcon',
            variant: 'danger',
            text: error?.response?.data?.message || 'Failed to generate report',
          },
        })
      } finally {
        type === 'preview'
          ? (this.isAttendanceReportGenerating = false)
          : (this.isAttendanceReportDownloading = false)
      }
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.attendance-preview table {
  border: 1px solid #ddd;
  font-size: 0.9rem;
}

.attendance-preview th,
.attendance-preview td {
  padding: 4px 6px;
  text-align: center;
}

.attendance-preview th {
  background-color: #f7f7f7;
  font-weight: 600;
}

.info-summery-table td {
  padding: 4px 6px;
}

.attendance-preview tr:nth-child(even) {
  background-color: #f9f9f9;
}

/* Dark mode */
.dark-layout .attendance-preview {
  background-color: #1f1f2e;
  color: #f0f0f0;
  border: 1px solid #444;
  border-radius: 6px;
}

.dark-layout .attendance-preview th {
  background-color: #2c2c3e;
  color: #f0f0f0;
  font-weight: 600;
}

.dark-layout .attendance-preview td {
  border: 1px solid #444;
  color: #f0f0f0;
}

.dark-layout .attendance-preview tr:nth-child(even) {
  background-color: #252537;
}

/* Mobile */
@media (max-width: 768px) {
  .attendance-preview table,
  .attendance-preview th,
  .attendance-preview td {
    font-size: 0.75rem;
    padding: 3px 5px;
  }

  .attendance-preview h3 {
    font-size: 1.4rem;
  }

  .attendance-preview .btn {
    font-size: 0.85rem;
    padding: 4px 8px;
  }
}
</style>

<style scoped lang="scss">
/* ✅ Responsive Adjustments (No Functionality Change) */

@media (max-width: 767.98px) {
  /* make selects and pickers full width on mobile */
  .v-select,
  .form-control {
    width: 100% !important;
  }

  /* add spacing between stacked filters */
  .b-form-group,
  .b-col {
    margin-bottom: 0.75rem !important;
  }

  /* ensure button fills width nicely on mobile */
  button.w-100 {
    font-size: 0.9rem;
  }

  /* space between elements in small screen */
  .d-flex.flex-wrap.justify-content-end {
    justify-content: center !important;
  }
}

@media (max-width: 575.98px) {
  /* make date range pickr a bit wider on small screens */
  .flatpickr-input {
    width: 90% !important;
    margin: 0 auto;
    display: block;
  }
}
</style>